import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Play, ExternalLink, Youtube } from "lucide-react";

const ADNC_CHANNEL_URL = "https://www.youtube.com/@AfricanDiasporaNews";

const videos = [
  {
    id: "Mg7fAeMpZHY",
    title: "The Black Man Who Built The Foundation of Artificial Intelligence — Honored by Nvidia",
    description:
      "Shea Richburg explores the untold story of the Black innovator whose work shaped modern AI, and what it means for the future.",
    duration: "PT12M",
    uploadDate: "2025-01-15",
  },
  {
    id: "FjW7LNZtkl4",
    title: "The Government May Pay You Because of AI",
    description:
      "A deep dive into Universal Basic Income (UBI), AI-driven job displacement, and what it could mean for American families.",
    duration: "PT10M",
    uploadDate: "2025-02-20",
  },
  {
    id: "EGLUQMn9iJ0",
    title: "Black America & The 3-Year Degree: Opportunity or Trap?",
    description:
      "Shea breaks down how AI is reshaping higher education and whether shorter degrees protect or threaten Black economic mobility.",
    duration: "PT11M",
    uploadDate: "2025-03-10",
  },
  {
    id: "RHJnxbrSaXg",
    title: "Humanoid Robots Are Coming For Warehouse Jobs",
    description:
      "The reality of automation in warehouses and what workers can do today to pivot into the AI economy before disruption hits.",
    duration: "PT9M",
    uploadDate: "2025-04-05",
  },
];

function VideoCard({ video, index, isInView }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const watchUrl = `https://www.youtube.com/watch?v=${video.id}`;
  const embedUrl = `https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0`;
  // Use hqdefault — reliably available for all videos (maxresdefault sometimes 404s)
  const thumbUrl = `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`;

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
      className="neon-card overflow-hidden group flex flex-col"
      data-testid={`video-card-${video.id}`}
    >
      {/* Video thumbnail / player */}
      <div className="relative aspect-video bg-black/60">
        {!isPlaying ? (
          <button
            onClick={() => setIsPlaying(true)}
            className="absolute inset-0 w-full h-full group/play"
            aria-label={`Play: ${video.title}`}
            data-testid={`video-play-${video.id}`}
          >
            <img
              src={thumbUrl}
              alt={`${video.title} — featured on African Diaspora News Channel with Shea Richburg`}
              loading="lazy"
              className="w-full h-full object-cover opacity-90 group-hover/play:opacity-100 transition-opacity"
            />
            {/* Dark gradient + play icon overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="w-20 h-20 rounded-full bg-primary/95 flex items-center justify-center shadow-[0_0_40px_rgba(168,85,247,0.6)] group-hover/play:bg-primary"
              >
                <Play className="w-9 h-9 text-primary-foreground fill-current ml-1" />
              </motion.div>
            </div>
            {/* Channel badge */}
            <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-black/70 backdrop-blur-sm border border-primary/30">
              <Youtube className="w-3.5 h-3.5 text-red-500" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-white">
                ADNC
              </span>
            </div>
          </button>
        ) : (
          <iframe
            src={embedUrl}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            className="absolute inset-0 w-full h-full"
            data-testid={`video-iframe-${video.id}`}
          />
        )}
      </div>

      {/* Card body */}
      <div className="p-5 lg:p-6 flex flex-col flex-1">
        <h3 className="font-['Orbitron'] text-base lg:text-lg font-bold text-foreground mb-2 leading-snug line-clamp-2">
          {video.title}
        </h3>
        <p className="text-sm text-muted-foreground mb-4 line-clamp-3 flex-1">
          {video.description}
        </p>
        <a
          href={watchUrl}
          target="_blank"
          rel="noopener"
          className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-fuchsia-300 transition-colors group/link"
          data-testid={`video-watch-link-${video.id}`}
        >
          Watch on YouTube
          <ExternalLink className="w-4 h-4 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" />
        </a>
      </div>
    </motion.article>
  );
}

export default function VideosSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  // JSON-LD VideoObject schema for SEO (rich snippets + topic authority)
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": videos.map((v) => ({
      "@type": "VideoObject",
      name: v.title,
      description: v.description,
      thumbnailUrl: [
        `https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`,
        `https://i.ytimg.com/vi/${v.id}/maxresdefault.jpg`,
      ],
      uploadDate: v.uploadDate,
      duration: v.duration,
      contentUrl: `https://www.youtube.com/watch?v=${v.id}`,
      embedUrl: `https://www.youtube.com/embed/${v.id}`,
      publisher: {
        "@type": "Organization",
        name: "African Diaspora News Channel",
        url: ADNC_CHANNEL_URL,
      },
      author: {
        "@type": "Person",
        name: "Shea Richburg",
        url: "https://shearichburg.com",
      },
    })),
  };

  return (
    <section
      ref={ref}
      id="videos"
      className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24 relative overflow-hidden"
      data-testid="videos-section"
    >
      {/* JSON-LD schema for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      {/* Background flourishes */}
      <div className="absolute left-0 top-1/3 w-[500px] h-[500px] gradient-blob opacity-50" />
      <div className="absolute right-1/4 bottom-0 w-[400px] h-[400px] gradient-blob opacity-40" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/50 bg-primary/10 mb-6">
            <Youtube size={16} className="text-red-500" />
            <span className="text-xs font-['Orbitron'] uppercase tracking-[0.15em] text-primary">
              Featured on African Diaspora News Channel
            </span>
          </div>
          <h2 className="font-['Orbitron'] text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-foreground mb-6">
            Shea On <span className="gradient-text">Camera</span>
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto">
            Watch Shea Richburg unpack AI, the future of work, education, and economic
            ownership in featured segments on{" "}
            <a
              href={ADNC_CHANNEL_URL}
              target="_blank"
              rel="noopener"
              className="text-primary hover:text-fuchsia-300 transition-colors underline-offset-4 hover:underline"
              data-testid="videos-adnc-channel-link"
            >
              African Diaspora News Channel
            </a>
            .
          </p>
        </motion.div>

        {/* Video Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {videos.map((video, index) => (
            <VideoCard key={video.id} video={video} index={index} isInView={isInView} />
          ))}
        </div>

        {/* CTA to channel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 text-center"
        >
          <a
            href={ADNC_CHANNEL_URL}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-3 px-6 py-3 rounded-full border border-primary/40 bg-primary/5 hover:bg-primary/10 transition-colors text-foreground font-semibold"
            data-testid="videos-subscribe-cta"
          >
            <Youtube className="w-5 h-5 text-red-500" />
            Subscribe to ADNC for more
            <ExternalLink className="w-4 h-4 text-primary" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
